import { View as MnView } from "extensions/marionette";
import { Backbone } from "extensions/backbone";
import { _ } from "extensions/underscore";
import { captureBreadcrumb, captureException } from "extensions/sentry";

import { request } from "common/components/request";
import strings from "common/strings";
import { authentication, users } from "common/config/api";

import validator from "./validator.jst";
import "./validator.scss";

const ValidatorRequirements = [
    {
        title: strings.login.validator.charactersLimit,
        rule(current) {
            const len = current.trim().length;
            return len >= 8;
        }
    },
    
    {
        title: strings.login.validator.alphabetical,
        rule(current) {
            return (/[A-z]/).test(current);
        }
    },
    
    {
        title: strings.login.validator.number,
        rule(current) {
            return (/.*\d.*/).test(current);
        }
    },
    
    {
        title: strings.login.validator.email,
        async: true,
        rule: _.debounce((current, token, resolve = false, reject = false) => {
            if (!current.trim().length) {
                return reject && reject(new Error("Password cannot be empty"));
            }
            
            // If token is provided then use it instead the security auth one
            request(users.me, {
                ensureAuth: !token,
                headers: !token ? {} : {
                    Authorization: token.includes(".") ? `JWT ${token}` : token
                }
            }).then((user) => {
                if (!resolve) {
                    return;
                }
                if (user.username !== current) {
                    resolve(true);
                } else {
                    reject(new Error("Password should be different from email address"));
                }
            }).catch(() => {
                if (reject) {
                    reject(new Error("Unable to compare username with password"));
                }
            });
        }, 1000)
    },
    
    {
        title: strings.login.validator.different,
        async: true,
        /**
         * Debounced asynchronous rule.
         * @param {String} current Password to validate
         * @param {String} token Valid authorization token
         */
        rule: _.debounce((current, token, resolve = false, reject = false) => {
            request(authentication.password.validate, {
                ensureAuth: !token,
                method: "POST",
                headers: !token ? {} : {
                    Authorization: token.includes(".") ? `JWT ${token}` : token
                },
                data: {
                    password: current
                }
            }).then(() => {
                if (resolve) {
                    resolve(true);
                }
            }).catch((error) => {
                if ([409, 400].includes(error.response.status) === false) {
                    // It is a different error than user is trying to use the same password. We should log it
                    captureException(error);
                }
                if (reject) {
                    reject(error);
                }
            });
        }, 1000)
    }
];

const runAsyncRule = function(rule, value, token) {
    return new Promise(function(resolve, reject) {
        rule.call(this, value, token, resolve, reject);
    });
};

/**
 * Basic validator model
 */
export const Model = Backbone.Model.extend({
    defaults: {
        value: ""
    }
});

/**
 * Validator view
 */
export const View = MnView.extend({
    template: validator,
    
    modelEvents: {
        change: "check"
    },
    
    initialize() {
        this.validatorRequirements = this.getOption("requirements") || ValidatorRequirements;
    },
    
    check() {
        this.checkRequirements();
        this.render();
    },
    
    /**
     * Check if all async and sync requirements pass
     */
    checkIfAllPass() {
        const allRequirementsMet = !_.some(this.validatorRequirements, (r) => {
            if (r.async) {
                return !(r.passed && r.passed.isFulfilled());
            }
            return !r.passed;
        });
        
        this.trigger("requirements", allRequirementsMet);
    },
    
    checkRequirements() {
        const value = this.model.get("value");
        _.each(this.validatorRequirements, function(requirement) {
            if (requirement.async) {
                requirement.passed = runAsyncRule(requirement.rule, value, this.getOption("token"));
                
                // Check if button should be enabled after requirement is fulfilled
                requirement.passed.then(this.checkIfAllPass.bind(this), (error) => {
                    captureBreadcrumb({
                        message: error.message,
                        category: "password-validator"
                    });
                });
            } else {
                requirement.passed = requirement.rule(value);
            }
        }, this);
        
        this.checkIfAllPass();
    },
    
    serializeData() {
        const PENDING_CLASS = "pending";
        const PASSED_CLASS = "passed";
        const FAILED_CLASS = "failed";
        
        return {
            requirements: this.validatorRequirements.map((requirement) => {
                const _id = _.uniqueId("a");
                const className = [];
                
                if (requirement.async && requirement.passed) {
                    className.push(PENDING_CLASS);
                    requirement.passed.then(() => {
                        this.$el.find(`#${_id}`).removeClass(PENDING_CLASS).removeClass(FAILED_CLASS).addClass(PASSED_CLASS);
                    }, () => {
                        this.$el.find(`#${_id}`).removeClass(PENDING_CLASS).removeClass(PASSED_CLASS).addClass(FAILED_CLASS);
                    });
                } else if (requirement.passed) {
                    className.push(PASSED_CLASS);
                } else {
                    className.push(FAILED_CLASS);
                }
                
                return {
                    title: requirement.title,
                    passed: requirement.passed,
                    async: requirement.async,
                    className: className.join(" "),
                    id: _id
                };
            })
        };
    }
});
